/* src/components/pages/OverwatchMetrics.jsx - Modificado el 15-03-2026 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Stack,
  Card,
  CardContent,
  Typography,
  Skeleton,
} from "@mui/material";

import api from "../../services/api.js";
import { useFeedback } from "../../context/FeedbackContext";
import { animate } from "framer-motion";
import { LineChart, Line, Area, ResponsiveContainer } from "recharts";

/* ======================================================
   Helpers parsing
====================================================== */

function extractMetricByTwoLabels(
  text,
  metricName,
  labelA,
  valueA,
  labelB,
  valueB
) {
  const regex = new RegExp(
    `${metricName}\\{[^}]*${labelA}="${valueA}"[^}]*${labelB}="${valueB}"[^}]*\\}\\s+(\\d+(?:\\.\\d+)?)`
  );
  const match = text.match(regex);
  return match ? Number(match[1]) : 0;
}

function sumMetricByLabel(text, metricName, labelKey, labelValue) {
  const regex = new RegExp(
    `${metricName}\\{[^}]*${labelKey}="${labelValue}"[^}]*\\}\\s+(\\d+(?:\\.\\d+)?)`,
    "g"
  );

  let total = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    total += Number(match[1]);
  }

  return total;
}

function extractSimpleMetric(text, metricName) {
  const regex = new RegExp(
    `${metricName}\\s+(\\d+(?:\\.\\d+)?)`,
    "g"
  );

  const matches = [...text.matchAll(regex)];

  if (!matches.length) return 0;

  // 🔥 tomar la ÚLTIMA (la más reciente)
  return Number(matches[matches.length - 1][1]);
}

function mapBatchStatus(status) {
  if (status === 2) return { label: "RUNNING", color: "#3b82f6" };
  if (status === 1) return { label: "OK", color: "#22c55e" };
  if (status === 0) return { label: "FAIL", color: "#ef4444" };
  return { label: "IDLE", color: "#6b7280" };
}

/* promedio desde histogram */

function getHistogramAvg(text, metricName, queueName) {
  const sumRegex = new RegExp(
    `${metricName}_sum\\{[^}]*queue="${queueName}"[^}]*\\}\\s+(\\d+(?:\\.\\d+)?)`
  );

  const countRegex = new RegExp(
    `${metricName}_count\\{[^}]*queue="${queueName}"[^}]*\\}\\s+(\\d+(?:\\.\\d+)?)`
  );

  const sumMatch = text.match(sumRegex);
  const countMatch = text.match(countRegex);

  const sum = sumMatch ? Number(sumMatch[1]) : 0;
  const count = countMatch ? Number(countMatch[1]) : 0;

  if (!count) return 0;
  return sum / count;
}

/* ======================================================
   Visual helpers
====================================================== */

function getSeverityColor(value, type) {
  if (type === "errors") return value > 0 ? "#ef4444" : "#22c55e";
  if (type === "workers") return value > 0 ? "#22c55e" : "#ef4444";

  if (type === "rate") {
    if (value >= 95) return "#22c55e";
    if (value >= 80) return "#f59e0b";
    return "#ef4444";
  }

  if (type === "duration") {
    if (value >= 60) return "#ef4444";
    if (value >= 25) return "#f59e0b";
    return "#22c55e";
  }

  if (type === "backlog") {
    if (value >= 800) return "#ef4444";
    if (value >= 400) return "#f59e0b";
    return "#22c55e";
  }

  return "#3b82f6";
}

/* ======================================================
   Animated number
====================================================== */

function AnimatedNumber({ value, suffix = "", decimals = 0 }) {
  const [display, setDisplay] = useState(value);
  const previous = useRef(value);

  useEffect(() => {
    // 🚨 FIX: evitar animación en valores muy chicos
    if (Math.abs(value - previous.current) < 0.01) {
      previous.current = value;
      setDisplay(value);
      return;
    }

    const controls = animate(previous.current, value, {
      duration: 0.6,
      onUpdate: (v) => setDisplay(v),
    });

    previous.current = value;
    return () => controls.stop();
  }, [value]);

  return (
    <>
      {Number(display).toFixed(decimals)}
      {suffix}
    </>
  );
}

/* ======================================================
   Metric history
====================================================== */

function useMetricHistory(initial = {}) {
  const historyRef = useRef(initial);

  const pushValue = (key, value, max = 20) => {
    if (!historyRef.current[key]) historyRef.current[key] = [];
    const arr = historyRef.current[key];
    arr.push({ v: value });
    if (arr.length > max) arr.shift();
  };

  return {
    history: historyRef.current,
    pushValue,
  };
}

/* ================= Sparkline ================= */

const Sparkline = ({ data, color, id }) => {
  if (!data || data.length < 2) return null;

  const gradientId = `sparkFill-${id}`;

  return (
    <Box sx={{ width: "100%", height: 26, mt: 0.75 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.35} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>

          <Line
            type="monotone"
            dataKey="v"
            stroke={color}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />

          <Area
            type="monotone"
            dataKey="v"
            stroke="none"
            fill={`url(#${gradientId})`}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

/* ======================================================
   Component
====================================================== */

const WORKER_CAPACITY = 2;

const OverwatchMetrics = () => {
  const [metrics, setMetrics] = useState(null);
  const { showFeedback } = useFeedback();
  const { history, pushValue } = useMetricHistory();

  const fetchMetrics = useCallback(async () => {
    try {
      const response = await api.get("/metrics", {
        headers: { Accept: "text/plain" },
      });

      const text = response.data;

      /* ===== Workers ===== */

      const ventasOk = extractMetricByTwoLabels(
        text,
        "overwatch_worker_jobs_processed_total",
        "queue",
        "libroVentasQueue",
        "status",
        "completed"
      );

      const ventasFailed = extractMetricByTwoLabels(
        text,
        "overwatch_worker_jobs_processed_total",
        "queue",
        "libroVentasQueue",
        "status",
        "failed"
      );

      const comprasOk = extractMetricByTwoLabels(
        text,
        "overwatch_worker_jobs_processed_total",
        "queue",
        "libroComprasQueue",
        "status",
        "completed"
      );

      const comprasFailed = extractMetricByTwoLabels(
        text,
        "overwatch_worker_jobs_processed_total",
        "queue",
        "libroComprasQueue",
        "status",
        "failed"
      );

      const cesionesOk = extractMetricByTwoLabels(
        text,
        "overwatch_worker_jobs_processed_total",
        "queue",
        "cesionesQueue",
        "status",
        "completed"
      );

      const cesionesFailed = extractMetricByTwoLabels(
        text,
        "overwatch_worker_jobs_processed_total",
        "queue",
        "cesionesQueue",
        "status",
        "failed"
      );

      /* ===== Backlog ===== */

      const backlogVentas = sumMetricByLabel(
        text,
        "overwatch_queue_backlog",
        "queue",
        "libroVentasQueue"
      );

      const backlogCompras = sumMetricByLabel(
        text,
        "overwatch_queue_backlog",
        "queue",
        "libroComprasQueue"
      );

      const backlogCesiones = sumMetricByLabel(
        text,
        "overwatch_queue_backlog",
        "queue",
        "cesionesQueue"
      );

      /* ===== Workers activos ===== */

      const workersVentas = sumMetricByLabel(
        text,
        "overwatch_worker_active_jobs",
        "queue",
        "libroVentasQueue"
      );

      const workersCompras = sumMetricByLabel(
        text,
        "overwatch_worker_active_jobs",
        "queue",
        "libroComprasQueue"
      );

      const workersCesiones = sumMetricByLabel(
        text,
        "overwatch_worker_active_jobs",
        "queue",
        "cesionesQueue"
      );

      /* ===== Worker stall detector ===== */

      const workersStalled =
        (backlogVentas + backlogCompras + backlogCesiones) > 0 &&
        (workersVentas + workersCompras + workersCesiones) === 0


      /* ===== Batch LAST ===== */

      const batchStatusNum = extractSimpleMetric(
        text,
        "overwatch_batch_last_status"
      );

      const batchLastRunTs = extractSimpleMetric(
        text,
        "overwatch_batch_last_run_timestamp"
      );

      const nowTs = Date.now() / 1000;
      const minutesSinceLastRun = batchLastRunTs
        ? (nowTs - batchLastRunTs) / 60
        : 999;

      const batchDead =
        batchStatusNum !== 2 && minutesSinceLastRun > 90;

      /* ===== Duración promedio ===== */

      const avgDurationVentas = getHistogramAvg(
        text,
        "overwatch_worker_job_duration_seconds",
        "libroVentasQueue"
      );

      const avgDurationCompras = getHistogramAvg(
        text,
        "overwatch_worker_job_duration_seconds",
        "libroComprasQueue"
      );

      const avgDurationCesiones = getHistogramAvg(
        text,
        "overwatch_worker_job_duration_seconds",
        "cesionesQueue"
      );

      const batchLastDuration = extractSimpleMetric(
        text,
        "overwatch_batch_last_duration_seconds"
      );


      
      const batchTrackingTotal = extractSimpleMetric(
        text,
        "overwatch_batch_tracking_total"
      );

      const batchVentasTotal = extractSimpleMetric(
        text,
        "overwatch_batch_jobs_ventas_total"
      );

      const batchComprasTotal = extractSimpleMetric(
        text,
        "overwatch_batch_jobs_compras_total"
      );

      /* ===== Sii inestable ===== */

      const sii503 = extractSimpleMetric(
        text,
        "overwatch_sii_503_total"
      );

      const siiCaptcha = extractSimpleMetric(
        text,
        "overwatch_sii_captcha_total"
      );

      const siiRetry = extractSimpleMetric(
        text,
        "overwatch_sii_retry_total"
      );


      /* ===== Rates ===== */

      const totalOk = ventasOk + comprasOk + cesionesOk;
      const totalFailed = ventasFailed + comprasFailed + cesionesFailed;

      const total = totalOk + totalFailed;

      const successRate =
        total > 0 ? Math.round((totalOk / total) * 100) : 100;

      const errorRate =
        total > 0 ? Math.round((totalFailed / total) * 100) : 0;

      /* ===== Throughput ===== */

      const prevOk = history.prevOk || 0;
      const deltaOk = Math.max(0, totalOk - prevOk);
      history.prevOk = totalOk;

      const throughputPerMin = Math.max(0, deltaOk * 4);

      /* ===== Worker saturation ===== */

      const activeWorkers =
        workersVentas +
        workersCompras +
        workersCesiones;

      const workerSaturation = Math.min(
        100,
        Math.round((activeWorkers / WORKER_CAPACITY) * 100)
      );

      const parsed = {
        ventasOk,
        ventasFailed,
        comprasOk,
        comprasFailed,
        backlogVentas,
        backlogCompras,
        workersVentas,
        workersCompras,
        avgDurationVentas,
        avgDurationCompras,
        successRate,
        errorRate,
        throughputPerMin,
        workerSaturation,
        batchStatusNum,
        batchDead,
        batchLastDuration,
        batchTrackingTotal,
        batchVentasTotal,
        batchComprasTotal,
        workersStalled,
        cesionesOk,
        cesionesFailed,
        backlogCesiones,
        workersCesiones,
        avgDurationCesiones,
        sii503,
        siiCaptcha,
        siiRetry,
      };

      setMetrics(parsed);

      pushValue("successRate", successRate);
      pushValue("errorRate", errorRate);
      pushValue("throughputPerMin", throughputPerMin);
      pushValue("workerSaturation", workerSaturation);
      pushValue("backlogCesiones", backlogCesiones);
    } catch (err) {
      console.error("❌ Error métricas:", err);

      showFeedback({
        message: "No fue posible obtener métricas Overwatch",
        severity: "error",
      });

      setMetrics(null);
    }
  }, [showFeedback, pushValue, history]);

  useEffect(() => {
    fetchMetrics();
    const id = setInterval(fetchMetrics, 15000);
    return () => clearInterval(id);
  }, [fetchMetrics]);

  if (!metrics) {
    return (
      <Stack direction="row" flexWrap="wrap" gap={2} justifyContent="center">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i} sx={{ width: 180, height: 96 }}>
            <CardContent>
              <Skeleton width="60%" />
              <Skeleton width="40%" height={32} />
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  }

  const batchVisual = mapBatchStatus(metrics.batchStatusNum);

  const renderCard = (titulo, valor, options = {}) => {
    const color = getSeverityColor(valor, options.type);
    const data = options.historyKey ? history[options.historyKey] : null;

    return (
      <Card
        sx={{
          width: 184,
          height: 116,
          backgroundColor: (theme) => theme.custom.ui.surfaceBg,
          borderRadius: 2,
          boxShadow: (theme) => theme.custom.ui.surfaceShadow,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CardContent sx={{ width: "100%", textAlign: "center" }}>
          <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 600 }}>
            {titulo}
          </Typography>

          <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 700, color }}>
            <AnimatedNumber
              value={valor}
              suffix={options.suffix || ""}
              decimals={options.decimals || 0}
            />
          </Typography>

          {data && <Sparkline data={data} color={color} id={titulo} />}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      {metrics.batchDead && (
        <Box
          sx={{
            mb: 2,
            p: 1.2,
            borderRadius: 2,
            backgroundColor: "#ef4444",
            color: "white",
            textAlign: "center",
            fontWeight: 700,
          }}
        >
          💀 BATCH NOCTURNO NO SE HA EJECUTADO (POSIBLE FALLA)
        </Box>
      )}

      {metrics.workersStalled && (
        <Box
          sx={{
            mb: 2,
            p: 1.2,
            borderRadius: 2,
            backgroundColor: "#ef4444",
            color: "white",
            textAlign: "center",
            fontWeight: 700,
          }}
        >
          🚨 BACKLOG CON WORKERS EN 0 (POSIBLE WORKER CAÍDO)
        </Box>
      )}

      <Stack direction="column" gap={3}>

        {/* ======================================================
            🔵 SALUD DEL SISTEMA
        ====================================================== */}
        <Box>
          <Typography
            variant="overline"
            sx={{ opacity: 0.7, fontWeight: 800, ml: 1 }}
          >
            Salud del Sistema
          </Typography>

          <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={2}>
            {renderCard("Success Rate", metrics.successRate, {
              type: "rate",
              suffix: "%",
              historyKey: "successRate",
            })}

            {renderCard("Error Rate", metrics.errorRate, {
              type: "errors",
              suffix: "%",
              historyKey: "errorRate",
            })}

            {renderCard("Throughput /min", metrics.throughputPerMin, {
              type: "workers",
              historyKey: "throughputPerMin",
            })}

            {renderCard("Worker Saturation", metrics.workerSaturation, {
              type: "rate",
              suffix: "%",
              historyKey: "workerSaturation",
            })}


            {renderCard("SII 503", metrics.sii503, {
              type: "errors"
            })}

            {renderCard("SII Retries", metrics.siiRetry)}

            {renderCard("SII Captcha", metrics.siiCaptcha, {
              type: "errors"
            })}

          </Stack>
        </Box>

        {/* ======================================================
            🟢 WORKERS CARD
        ====================================================== */}
        <Box>
          <Typography
            variant="overline"
            sx={{ opacity: 0.7, fontWeight: 800, ml: 1 }}
          >
            Workers
          </Typography>

          <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={2}>
            {renderCard("Workers Ventas", metrics.workersVentas, {
              type: "workers",
            })}

            {renderCard("Workers Compras", metrics.workersCompras, {
              type: "workers",
            })}

            {renderCard("Avg Ventas (s)", metrics.avgDurationVentas, {
              type: "duration",
              decimals: 2,
            })}

            {renderCard("Avg Compras (s)", metrics.avgDurationCompras, {
              type: "duration",
              decimals: 2,
            })}

            {renderCard("Backlog Ventas", metrics.backlogVentas, {
              type: "backlog",
              historyKey: "backlogVentas",
            })}

            {renderCard("Backlog Compras", metrics.backlogCompras, {
              type: "backlog",
              historyKey: "backlogCompras",
            })}

            {renderCard("Workers Cesiones", metrics.workersCesiones, {
              type: "workers",
            })}

            {renderCard("Avg Cesiones (s)", metrics.avgDurationCesiones, {
              type: "duration",
              decimals: 2,
            })}

            {renderCard("Backlog Cesiones", metrics.backlogCesiones, {
              type: "backlog",
              historyKey: "backlogCesiones",
            })}
          </Stack>
        </Box>

        {/* ======================================================
            🟣 BATCH
        ====================================================== */}
        <Box>
          <Typography
            variant="overline"
            sx={{ opacity: 0.7, fontWeight: 800, ml: 1 }}
          >
            Batch
          </Typography>

          <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={2}>
            <Card sx={{ width: 184, height: 116 }}>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="caption">Batch Status</Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 800, color: batchVisual.color }}
                >
                  {batchVisual.label}
                </Typography>
              </CardContent>
            </Card>

            {renderCard("Batch Last (s)", metrics.batchLastDuration, {
              type: "duration",
              decimals: 3,
            })}

            {renderCard("Tracking Leídos", metrics.batchTrackingTotal)}

            {renderCard("Batch Ventas Jobs", metrics.batchVentasTotal)}

            {renderCard("Batch Compras Jobs", metrics.batchComprasTotal)}
          </Stack>
        </Box>

      </Stack>


      {/* <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={2}>
        {renderCard("Success Rate", metrics.successRate, {
          type: "rate",
          suffix: "%",
          historyKey: "successRate",
        })}

        {renderCard("Error Rate", metrics.errorRate, {
          type: "errors",
          suffix: "%",
          historyKey: "errorRate",
        })}

        {renderCard("Throughput /min", metrics.throughputPerMin, {
          type: "workers",
          historyKey: "throughputPerMin",
        })}

        {renderCard("Worker Saturation", metrics.workerSaturation, {
          type: "rate",
          suffix: "%",
          historyKey: "workerSaturation",
        })}


        {renderCard("Workers Ventas", metrics.workersVentas, {
          type: "workers",
        })}

        {renderCard("Workers Compras", metrics.workersCompras, {
          type: "workers",
        })}

        {renderCard("Avg Ventas (s)", metrics.avgDurationVentas, {
          type: "duration",
          decimals: 2,
        })}

        {renderCard("Avg Compras (s)", metrics.avgDurationCompras, {
          type: "duration",
          decimals: 2,
        })}


        <Card sx={{ width: 184, height: 116 }}>
          <CardContent sx={{ textAlign: "center" }}>
            <Typography variant="caption">Batch Status</Typography>
            <Typography
              variant="h5"
              sx={{ fontWeight: 800, color: batchVisual.color }}
            >
              {batchVisual.label}
            </Typography>
          </CardContent>
        </Card>

        {renderCard("Batch Last (s)", metrics.batchLastDuration, {
          type: "duration",
          decimals: 3,
        })}

        {renderCard("Tracking Leídos", metrics.batchTrackingTotal)}

        {renderCard("Batch Ventas Jobs", metrics.batchVentasTotal)}

        {renderCard("Batch Compras Jobs", metrics.batchComprasTotal)}

      </Stack> */}


    </Box>
  );
};

export default OverwatchMetrics;
